import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  UserMinusIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  OffboardingRequestsClient,
  OffboardingRequestDto,
  RequestStatus
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { DataTable } from '../../components/ui/data-table';
import { Button } from '../../components/ui/button';
import { RequestStatusBadge } from '../../components/ui/status-badge';
import { ERROR_MESSAGES, LOADING_TEXT } from '../../constants/messages';
import NewOffboardingRequestForm from './NewOffboardingRequestForm';

type ViewMode = 'active' | 'completed';

export default function Offboarding() {
  const navigate = useNavigate();
  const [showOffboardingForm, setShowOffboardingForm] = useState(false);
  const [requests, setRequests] = useState<OffboardingRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('active');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter options for status - changes based on view mode
  const statusOptions = useMemo(() => {
    if (viewMode === 'active') {
      return [
        { value: 'all', label: 'All Active' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'inprogress', label: 'In Progress' },
      ];
    } else {
      return [
        { value: 'all', label: 'All Completed' },
      ];
    }
  }, [viewMode]);

  // Reset status filter when view mode changes
  useEffect(() => {
    setStatusFilter('all');
  }, [viewMode]);

  // Filtered requests based on view mode and status
  const filteredRequests = useMemo(() => {
    // First filter by view mode
    let filtered = requests.filter(r => {
      const status = Number(r.status);
      if (viewMode === 'active') {
        // Active: Draft, Pending, Approved, In Progress
        return status === RequestStatus.Draft ||
               status === RequestStatus.Pending ||
               status === RequestStatus.PendingFirstApproval ||
               status === RequestStatus.PendingSecondApproval ||
               status === RequestStatus.Approved ||
               status === RequestStatus.InProgress;
      } else {
        // Completed view: show only completed offboarding requests
        return status === RequestStatus.Completed;
      }
    });

    // Then apply status filter (only for active view)
    if (viewMode === 'active' && statusFilter !== 'all') {
      filtered = filtered.filter(r => {
        const status = Number(r.status);
        switch (statusFilter) {
          case 'draft':
            return status === RequestStatus.Draft;
          case 'pending':
            return status === RequestStatus.Pending ||
                   status === RequestStatus.PendingFirstApproval ||
                   status === RequestStatus.PendingSecondApproval;
          case 'approved':
            return status === RequestStatus.Approved;
          case 'inprogress':
            return status === RequestStatus.InProgress;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [requests, viewMode, statusFilter]);

  // Counts for the toggle badges
  const activeCount = useMemo(() => {
    return requests.filter(r => {
      const status = Number(r.status);
      return status === RequestStatus.Draft ||
             status === RequestStatus.Pending ||
             status === RequestStatus.PendingFirstApproval ||
             status === RequestStatus.PendingSecondApproval ||
             status === RequestStatus.Approved ||
             status === RequestStatus.InProgress;
    }).length;
  }, [requests]);

  const completedCount = useMemo(() => {
    return requests.filter(r => Number(r.status) === RequestStatus.Completed).length;
  }, [requests]);

  // Define table columns
  const columns: ColumnDef<OffboardingRequestDto>[] = [
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div>
            <div className="font-medium text-slate-900 dark:text-white">{row.original.employeeName}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {row.original.employeeNo || row.original.locationName}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'designation',
      header: 'Role & Dept',
      cell: ({ row }) => (
        <div>
          <div className="text-slate-900 dark:text-white">{row.original.designation}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{row.original.departmentName}</div>
        </div>
      ),
    },
    {
      accessorKey: 'lastWorkingDate',
      header: 'Last Working Day',
      cell: ({ row }) => (
        <div className="text-slate-600 dark:text-slate-300">
          {row.original.lastWorkingDate
            ? new Date(row.original.lastWorkingDate + 'Z').toLocaleDateString()
            : '-'}
        </div>
      ),
    },
    {
      id: 'actions_summary',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          {row.original.revokeCount! > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
              {row.original.revokeCount} Revoke
            </span>
          )}
          {row.original.returnCount! > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
              {row.original.returnCount} Return
            </span>
          )}
          {row.original.extendedCount! > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
              {row.original.extendedCount} Extended
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'progress',
      header: 'Progress',
      cell: ({ row }) => {
        const totalTasks = row.original.totalTasks || 0;
        const completedTasks = row.original.completedTasks || 0;
        const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        return (
          <div className="w-32">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium dark:text-gray-300">{completedTasks}/{totalTasks}</span>
              <span className="text-xs text-gray-500">({progress}%)</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <RequestStatusBadge status={row.original.status} />
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/offboarding/${row.original.id}`)}
            className="text-slate-600 hover:text-slate-900"
            title="View Details"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ];

  // Fetch offboarding requests list
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
        const data = await client.getOffboardingRequests();
        setRequests(data);
      } catch (error) {
        console.error(ERROR_MESSAGES.FETCH_FAILED('offboarding requests'), error);
        toast.error(ERROR_MESSAGES.FETCH_FAILED('offboarding requests'));
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  const handleOffboardingFormClose = () => {
    setShowOffboardingForm(false);
  };

  const handleOffboardingSuccess = async () => {
    // Refresh the requests list
    try {
      const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
      const data = await client.getOffboardingRequests();
      setRequests(data);
    } catch (error) {
      console.error('Failed to refresh offboarding requests:', error);
    }
  };

  // Stats calculations
  const totalRequests = requests.length;
  const inProgressCount = requests.filter(r =>
    Number(r.status) === RequestStatus.InProgress ||
    Number(r.status) === RequestStatus.Approved ||
    Number(r.status) === RequestStatus.Draft
  ).length;
  const totalItemsToReturn = requests.reduce((sum, r) => sum + (r.returnCount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Offboarding Requests</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Manage and track employee offboarding processes
            </p>
          </div>
          <button
            onClick={() => setShowOffboardingForm(true)}
            className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl shadow-lg hover:shadow-xl text-white font-medium transition-all duration-200 hover:scale-105"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Initiate Offboarding
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserMinusIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Requests</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">{totalRequests}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowPathIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">In Progress</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">{inProgressCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Completed</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">{completedCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Items to Return</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">{totalItemsToReturn}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offboarding Requests List */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 dark:text-gray-400">{LOADING_TEXT.DEFAULT}</div>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <UserMinusIcon className="h-12 w-12" />
            </div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">No offboarding requests yet</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Initiate an offboarding to get started</p>
            <div className="mt-6">
              <button
                onClick={() => setShowOffboardingForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Initiate Offboarding
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* View Mode Toggle & Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              {/* Active/Completed Toggle */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('active')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'active'
                      ? 'bg-white dark:bg-gray-600 text-red-700 dark:text-red-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <ClockIcon className="h-4 w-4" />
                  Active
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    viewMode === 'active'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {activeCount}
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('completed')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'completed'
                      ? 'bg-white dark:bg-gray-600 text-red-700 dark:text-red-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Completed
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    viewMode === 'completed'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {completedCount}
                  </span>
                </button>
              </div>

              {/* Status Filter (only for Active view) */}
              {viewMode === 'active' && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {statusFilter !== 'all' && (
                    <button
                      onClick={() => setStatusFilter('all')}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            <DataTable
              columns={columns}
              data={filteredRequests}
              searchable={true}
              searchColumn="employeeName"
              searchPlaceholder="Search by employee name..."
            />
          </>
        )}
      </div>

      {/* New Offboarding Request Form */}
      <NewOffboardingRequestForm
        show={showOffboardingForm}
        onClose={handleOffboardingFormClose}
        onSuccess={handleOffboardingSuccess}
      />
    </div>
  );
}
